import * as z from "zod";
import { Button } from "@/components/ui/button";
import { SchemaEntrada, type SchemaFlujoEntidadIn } from "./Flujo.schema";
import {
	SchemaEntradaRawCO2,
	SchemaEntradaMateriaPrima,
	SchemaEntradaProducto,
	SchemaEntradaSubProceso,
} from "./Entrada.schema";
import { TextInput } from "@/components/Inputs/TextInput";
import { SelectInput } from "@/components/Inputs/SelectInput";
import { useState } from "react";
import { useApi } from "@/hooks/useApi";
import { UnitSelect } from "@/components/Inputs/UnitSelect";


interface FormProps<T> {
	readonly value: z.input<T>;
	readonly onChange: (d: SchemaFlujoEntidadIn) => void;
	readonly onAdd: (d: SchemaFlujoEntidadIn) => void;	///Para subprocesos, que pueden añadir entradas y salidas. Esto es un poco raro, pero es lo que hay.
	readonly onDelete: (d: SchemaFlujoEntidadIn) => void;
	readonly errors: { [key in keyof z.input<T>]?: string[] };

	readonly referenciaProyecto: string;	// Para ProductoFields, que necesita filtrar los productos del mismo proyecto.
}
export function FormProyectoEntrada(props: FormProps<typeof SchemaEntrada>) {
	const { value, onChange, errors } = props;
	const handler: ChangeHandler<typeof SchemaEntrada> = (field, val) => {
		onChange({ ...value, [field]: val });
	};

	const handleDelete = () => {
		let message = `¿Está seguro de que desea eliminar la entrada "${value.nombre}"?`;
		if (value.subtipo == "sub_proceso") {
			message = `¿Está seguro de que desea eliminar el subproceso "${value.nombre}"? Esto eliminará todas las entradas y salidas asociadas.`;
		}
		if (window.confirm(message)) {
			props.onDelete(value);
		}
	};

	return (
		<form onSubmit={(e) => e.preventDefault()} className="space-y-4">
			<TextInput
				value={value.nombre}
				onChange={(e) => handler("nombre", e.target.value)}
				errors={errors.nombre}
				label="Nombre"
				placeholder="Nombre"
				type="text"
			/>
			<SelectInput
				value={value.subtipo}
				onChange={(e) => handler("subtipo", e as "raw_co2" | "materia_prima" | "producto" | "sub_proceso")}
				errors={errors.subtipo}
				label="Tipo"
				// placeholder="Tipo"
				options={{
					raw_co2: "CO2 Directo",
					materia_prima: "Materia Prima BDD",
					producto: "Producto Propio",
					sub_proceso: "Sub-Proceso",
				}}
			/>

			{value.subtipo == "raw_co2" && (
				<RawCO2Fields value={value as SchemaEntradaRawCO2} handler={handler} onChange={onChange} errors={errors} />
			)}
			{value.subtipo == "materia_prima" && (
				<MateriaPrimaFields value={value as SchemaEntradaMateriaPrima} handler={handler} onChange={onChange} errors={errors} />
			)}
			{value.subtipo == "producto" && (
				<ProductoFields value={value as SchemaEntradaProducto} handler={handler} onChange={onChange} errors={errors} referenciaProyecto={props.referenciaProyecto} />
			)}
			{value.subtipo == "sub_proceso" && (
				<SubProcesoFields
					value={value as SchemaEntradaSubProceso}
					handler={handler}
					onChange={onChange}
					errors={errors}
					onAdd={props.onAdd}
				/>
			)}
			<Button variant="destructive" onClick={handleDelete} className="w-full">Eliminar</Button>
		</form>
	);
}
interface SubFormProps<T> {
	readonly value: z.input<T>;
	readonly handler: ChangeHandler<T>;
	readonly onChange: (d: SchemaFlujoEntidadIn) => void;
	readonly errors: z.core.$ZodFlattenedError<z.input<T>>["fieldErrors"];
}
export function RawCO2Fields(props: SubFormProps<typeof SchemaEntradaRawCO2>) {
	const { value, handler, errors } = props;

	return (
		<TextInput
			value={String(value.cantidad)}
			onChange={(e) => handler("cantidad", e.target.value)}
			errors={errors.cantidad}
			label="Cantidad"
			placeholder="Cantidad"
			type="text"
		/>
	);
}
export function MateriaPrimaFields(props: SubFormProps<typeof SchemaEntradaMateriaPrima>) {
	const { value, handler, errors } = props;
	const [options] = useState<Record<string, string>>({
		[crypto.randomUUID()]: "Materia Prima 1",
		[crypto.randomUUID()]: "Materia Prima 2",
	});

	return (
		<>
			<SelectInput
				value={value.idMateriaPrima}
				onChange={(e) => handler("idMateriaPrima", e)}
				errors={errors.idMateriaPrima}
				label="Materia Prima"
				// placeholder="Producto"
				options={options}
			/>

			<TextInput
				value={String(value.cantidad)}
				onChange={(e) => handler("cantidad", e.target.value)}
				errors={errors.cantidad}
				label="Cantidad"
				placeholder="Cantidad"
				type="text"
			/>
		</>
	);
}
export function ProductoFields(props: SubFormProps<typeof SchemaEntradaProducto> & { readonly referenciaProyecto: string; }) {
	const { value, handler, onChange, errors } = props;
	const api = useApi();
	const productos = api.useQuery("get", "/v1/Empresa/Proyectos/Productos", {});
	const options = productos.data
		?.filter((producto) => producto.referenciaProyecto != props.referenciaProyecto)
		.reduce((acc, producto) => {
			acc[producto.id] = producto.nombre;
			return acc;
		}, {} as Record<string, string>) ?? {};

	const seleccionado = productos.data?.find((producto) => producto.id === value.idProducto);
	const onVerProceso = () => {
		if (!seleccionado) {
			alert("No se ha seleccionado ningún producto o el producto no existe.");
			return;
		}
		window.open(`/user/proyectos/${seleccionado.referenciaProyecto}`, "_blank");
	};

	const onChangeProducto = (idProducto: string) => {
		const producto = productos.data?.find((p) => p.id === idProducto);
		onChange({
			...value,
			idProducto,
			unidad: producto?.unidad ?? "g",
		});
	};

	return (
		<>
			<SelectInput
				value={value.idProducto}
				onChange={onChangeProducto}
				errors={errors.idProducto}
				label="Producto"
				// placeholder="Producto"
				options={options}
			/>

			<TextInput
				value={String(value.cantidad)}
				onChange={(e) => handler("cantidad", e.target.value)}
				errors={errors.cantidad}
				label="Cantidad"
				placeholder="Cantidad"
				type="text"
			/>

			<Button variant="secondary" className="w-full" onClick={onVerProceso}>
				Ver proceso
			</Button>
		</>
	);
}
interface SubprocesoProps { readonly onAdd: (d: SchemaFlujoEntidadIn) => void; }
export function SubProcesoFields(props: SubFormProps<typeof SchemaEntradaSubProceso> & SubprocesoProps) {
	const { value, handler, errors } = props;

	const handleEntrada = () => {
		const newEntrada: SchemaFlujoEntidadIn = {
			id: crypto.randomUUID(),
			nombre: "Nueva Entrada",
			tipo: "entrada",
			subtipo: "raw_co2",
			unidad: "g",
			cantidad: 0,
			idProyecto: value.idProyecto,
			idProcesoTo: value.id,
		};
		props.onAdd(newEntrada);
	};

	const handleSalida = () => {
		const newSalida: SchemaFlujoEntidadIn = {
			id: crypto.randomUUID(),
			nombre: "Nueva Salida",
			tipo: "salida",
			subtipo: "raw_co2",
			unidad: "g",
			cantidad: 0,
			idProyecto: value.idProyecto,
			idProcesoFrom: value.id,
		};
		props.onAdd(newSalida);
	};

	const handleUnitChange: Setter<string> = (valOrFunc) => {
		const val = typeof valOrFunc === "function" ? valOrFunc(value.unidad) : valOrFunc;
		handler("unidad", val);
	};

	return (
		<>
			<UnitSelect
				value={value.unidad}
				onChange={handleUnitChange}
				errors={errors.unidad}
				label="Unidad"
				placeholder="Unidad"
			/>

			<TextInput
				value={String(value.cantidad)}
				onChange={(e) => handler("cantidad", e.target.value)}
				errors={errors.cantidad}
				label="Cantidad"
				placeholder="Cantidad"
				type="text"
			/>

			<SelectInput
				value={value.externo}
				onChange={(e) => handler("externo", e as "propio" | "externo")}
				errors={errors.externo}
				label="Tipo"
				// placeholder="Tipo"
				options={{
					propio: "Propio",
					externo: "Externo",
				}}
			/>
			<SelectInput
				value={value.alcance}
				onChange={(e) => handler("alcance", e as "cuna_puerta" | "puerta_puerta")}
				errors={errors.alcance}
				label="Alcance"
				// placeholder="Alcance"
				options={{
					cuna_puerta: "De cuna a puerta",
					puerta_puerta: "De puerta a puerta",
				}}
			/>

			<Button variant="secondary" onClick={handleEntrada} className="w-full">Añadir Entrada</Button>
			<Button variant="secondary" onClick={handleSalida} className="w-full">Añadir Salida</Button>
		</>
	);
}


type ChangeHandler<T> = <K extends keyof z.input<T>>(field: K, val: z.input<T>[K]) => void;
